/**
 * Derive the active tenant slug from the browser host.
 *
 * Per-tenant subdomains make the host the authoritative tenant selector:
 * production hosts are `<tenant-slug>.<base>` (e.g. `acme.iwarranty.ai`, or
 * `acme.portal.launchpad.iwarranty.ai`), so the leftmost DNS label IS the
 * tenant slug. A generic/platform host (`app.iwarranty.ai`, `www`, `portal`,
 * `localhost`, ...) carries NO tenant - those surfaces stay multi-tenant
 * (the consumer wallet, the all-products lookup, the generic login).
 *
 * Dev: `<tenant>.localhost[:port]` yields `<tenant>`; bare `localhost` yields
 * `null`. This mirrors the backend `TenantSubdomainMiddleware._subdomain_label`,
 * which treats the leftmost label of `<label>.<base>` as the slug, and the
 * generic entries of `RESERVED_TENANT_SLUGS` in
 * `backend/apps/organisations/validators.py`.
 */

// Leftmost labels that are platform surfaces, never tenant slugs. Kept in sync
// with the generic entries of RESERVED_TENANT_SLUGS on the backend.
const RESERVED_HOST_LABELS = new Set([
  'app',
  'www',
  'web',
  'portal',
  'api',
  'admin',
  'upload',
  'auth',
  'login',
  'static',
  'assets',
  'cdn',
  'localhost',
])

const IPV4_RE = /^\d{1,3}(?:\.\d{1,3}){3}$/

/**
 * The tenant slug encoded in `hostname`, or `null` when the host is generic.
 *
 * @param hostname defaults to `window.location.hostname` (port already excluded)
 */
export function getTenantSlugFromHost(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): string | null {
  const host = (hostname || '').trim().toLowerCase()
  if (!host || host === 'localhost') return null
  if (IPV4_RE.test(host)) return null

  const labels = host.split('.')
  // Need `<label>.<something>` for a subdomain to exist.
  if (labels.length < 2) return null

  const label = labels[0]
  if (!label || RESERVED_HOST_LABELS.has(label)) return null
  return label
}

/** Whether the current host pins the session to a single tenant. */
export function isTenantSubdomain(
  hostname?: string,
): boolean {
  return getTenantSlugFromHost(hostname) !== null
}

/**
 * Absolute URL for another tenant on the SAME environment as the current host.
 *
 * Used for cross-tenant partner switching: a partner user with orgs in two
 * tenants opens each in its own `<slug>.domain` tab rather than swapping
 * headers in place. Derived by swapping the leftmost label of the current host
 * (e.g. `dealer.portal.example` -> `acme.portal.example`, `dealer.localhost:5190`
 * -> `acme.localhost:5190`).
 *
 * Returns `null` when the current host is generic (no leading tenant label) -
 * the environment base is then unknown, so the caller falls back to in-place
 * switching.
 */
export function buildTenantUrl(slug: string, path = '/'): string | null {
  if (typeof window === 'undefined' || !slug) return null
  const { protocol, hostname, port } = window.location
  const current = getTenantSlugFromHost(hostname)
  if (!current) return null
  const base = hostname.slice(current.length + 1) // strip "<current>."
  const host = port ? `${slug}.${base}:${port}` : `${slug}.${base}`
  const normalisedPath = path.startsWith('/') ? path : `/${path}`
  return `${protocol}//${host}${normalisedPath}`
}
